import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { useGetOrderQuery } from '@/features/checkout/ordersApi';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { formatTimeInTz, restaurantTz } from '@/lib/hours';
import { addLine, openDrawer } from '@/features/cart/cartSlice';
import { computeUnitPrice } from '@/features/menu/computeUnitPrice';
import { inGroupOrder } from '@/features/menu/quickAddToCart';
import { localizableSnapshot, withCartCode } from '@/features/cart/cartCode';
import type { CartLine, SelectedOption } from '@/types/cart';
import type { Menu, OptionGroup } from '@/types/menu';
import type { Order } from '@/types/order';
import { statusLabel, paymentLabel } from '@/features/checkout/orderLabels';
import { orderDisplayNumber } from '@/features/checkout/orderNumber';
import Price, { formatPrice } from '@/components/ui/Price';
import CancellationPolicyNotice from '@/features/restaurant/CancellationPolicyNotice';


type ResolvedLine = { line: CartLine; priceChanged: boolean };
type ResolveResult = { resolved: ResolvedLine[]; skipped: string[] };

// Re-resolves a historical order line against the live menu. The order payload
// flattens all selected options into one synthetic group with empty groupId
// (see ordersApi.transformRawOrder), so we re-attach each optionId to its
// current owning group and recompute the unit price from current catalog
// prices. A line is skipped if the product or any selected option is no longer
// in the menu — that's a "reconfirmation needed" case.
function resolveLineAgainstMenu(line: CartLine, menu: Menu): CartLine | null {
  const menuItem = menu.items.find((it) => it.id === line.itemId);
  if (!menuItem) return null;

  // Per-product resolved groups (per-product price + is_active); fall back to
  // the shared list only for older menus that lack item.optionGroups. (SC-97)
  const liveGroupsForItem =
    menuItem.optionGroups ??
    menuItem.optionGroupIds
      .map((gid) => menu.optionGroups.find((g) => g.id === gid))
      .filter((g): g is OptionGroup => Boolean(g));

  const flatEntries = line.selectedOptions.flatMap((s) => s.options);
  const resolvedSelected: SelectedOption[] = [];
  for (const entry of flatEntries) {
    const owning = liveGroupsForItem.find((g) => g.options.some((o) => o.id === entry.id));
    if (!owning) return null;
    const bucket = resolvedSelected.find((s) => s.groupId === owning.id);
    if (bucket) {
      const existing = bucket.options.find((o) => o.id === entry.id);
      if (existing) existing.qty += entry.qty;
      else bucket.options.push({ id: entry.id, qty: entry.qty });
    } else {
      resolvedSelected.push({ groupId: owning.id, options: [{ id: entry.id, qty: entry.qty }] });
    }
  }

  return {
    ...line,
    lineId: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `line-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    unitPrice: computeUnitPrice(menuItem, liveGroupsForItem, resolvedSelected),
    // SC-1507 / DF-0518 — dans l'ordre des questions du plat, pas dans celui de la commande d'origine :
    // une commande passée avant le correctif ressortirait sinon en cuisine avec le bol 1 coupé en deux.
    selectedOptions: inGroupOrder(resolvedSelected, liveGroupsForItem),
    snapshot: { name: menuItem.name, photo: menuItem.photo, code: menuItem.code },
  };
}

function resolveOrderAgainstMenu(order: Order, menu: Menu): ResolveResult {
  const resolved: ResolvedLine[] = [];
  const skipped: string[] = [];
  for (const line of order.lines) {
    const fresh = resolveLineAgainstMenu(line, menu);
    if (!fresh) skipped.push(line.snapshot.name);
    else resolved.push({ line: fresh, priceChanged: fresh.unitPrice !== line.unitPrice });
  }
  return { resolved, skipped };
}

export default function OrderDetailPage() {
  const { t } = useTranslation();
  const { restaurantUuid = '', orderId = '' } = useParams<{ restaurantUuid: string; orderId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: order, isLoading, isError } = useGetOrderQuery(orderId, { skip: !orderId });
  // SC-1081 — heures de la commande lues a l'horloge du restaurant.
  const { data: identity } = useGetRestaurantIdentityQuery(restaurantUuid, { skip: !restaurantUuid });
  const tz = restaurantTz(identity ?? {});
  // refetchOnMountOrArgChange: the shell-level CartDrawer keeps the menu query
  // subscribed for the whole session, so without an explicit refetch the cache
  // outlives any back-office price change. Recommander must use current prices
  // — re-fetch on mount and gate the button on isFetching to avoid clicking
  // during the background refetch.
  const { data: menu, isFetching: isMenuFetching } = useGetMenuQuery(
    restaurantUuid,
    { skip: !restaurantUuid, refetchOnMountOrArgChange: true },
  );

  // Holds the resolved cart payload while the user reviews skipped items. Null
  // means no reorder is in progress.
  const [pending, setPending] = useState<ResolveResult | null>(null);

  function commit(resolved: ResolvedLine[]) {
    for (const { line } of resolved) {
      dispatch(addLine({ restaurantUuid, line }));
    }
    dispatch(openDrawer());
    navigate(`/${restaurantUuid}`);
  }

  function handleRecommander() {
    if (!order || !menu) return;
    const result = resolveOrderAgainstMenu(order, menu);
    if (result.skipped.length === 0) {
      commit(result.resolved);
    } else {
      // Surface the unavailable items and let the user decide. Price-only
      // changes don't trigger the banner — the new total is visible in the
      // cart drawer once they continue.
      setPending(result);
    }
  }

  const backLink = `/${restaurantUuid}/account/orders`;

  if (isLoading) {
    return (
      <div
        className="bg-paper border-2 border-ink shadow-stamp p-6 rounded-btn space-y-4"
        data-testid="order-detail-skeleton"
      >
        <div className="h-4 bg-ink/10 rounded" />
        <div className="h-4 bg-ink/10 rounded w-3/4" />
        <div className="h-4 bg-ink/10 rounded w-1/2" />
      </div>
    );
  }

  if (!isError && order) {
    if (order.restaurantUuid !== restaurantUuid) {
      return (
        <div className="bg-paper border-2 border-ink shadow-stamp p-6 rounded-btn text-center space-y-3">
          <p className="font-display text-ink">{t('orderDetail.wrongRestaurant')}</p>
          <Link to={backLink} className="underline text-sm">{t('orderDetail.backToOrders')}</Link>
        </div>
      );
    }

    const hideEta = order.status === 'handed_over' || order.status === 'cancelled';
    const reorderDisabled = order.lines.length === 0 || isMenuFetching || !menu;
    const reorderTitle = order.lines.length === 0
      ? t('orderDetail.reorderTitleEmpty')
      : (!menu || isMenuFetching) ? t('orderDetail.reorderTitleLoading') : undefined;

    return (
      <div className="bg-paper border-2 border-ink shadow-stamp p-6 rounded-btn space-y-4">
        {/* Header */}
        <div>
          <Link to={backLink} className="underline text-sm">{t('orderDetail.backToOrders')}</Link>
          <h2 className="font-display text-2xl mt-2">
            {t('orderDetail.orderNumber', { id: orderDisplayNumber(order) })}
          </h2>
        </div>

        {/* Status row */}
        <p className="font-mono text-sm text-ink/80">
          {statusLabel(order.status, t)}
          {' · '}
          {paymentLabel(order.paymentStatus, t)}
          {' · '}
          {t(order.mode === 'delivery' ? 'order.modeDelivery' : 'order.modeTakeaway')}
          {' · '}
          {new Date(order.createdAt).toLocaleString('fr-FR', { timeZone: tz })}
          {order.isAsap && (
            <> · <span data-testid="order-detail-asap">{t('checkout.slotAsap')}</span></>
          )}
          {!hideEta && order.estimatedReadyAt && (
            <> · {t('orderDetail.estimate', { time: formatTimeInTz(order.estimatedReadyAt, tz) })}</>
          )}
        </p>

        {order.status !== 'handed_over' && order.status !== 'cancelled' && (
          <CancellationPolicyNotice restaurantUuid={restaurantUuid} />
        )}

        {/* Line items */}
        <div>
          <h3 className="font-display text-lg mb-2">{t('orderDetail.articles')}</h3>
          <ul className="font-mono text-sm space-y-2">
            {order.lines.map((line) => (
              <li key={line.lineId}>
                <div className="flex justify-between">
                  <span>{line.qty}× {withCartCode(localizableSnapshot(line, menu))}</span>
                  <Price amountCents={line.unitPrice * line.qty} />
                </div>
                {line.optionNames && line.optionNames.length > 0 && (
                  <ul className="list-none pl-4 m-0 text-xs text-ink/70 leading-tight">
                    {line.optionNames.map((name, i) => (
                      <li key={i}>· {name}</li>
                    ))}
                  </ul>
                )}
                {line.note && (
                  <p className="m-0 pl-4 text-xs italic text-ink/60">“{line.note}”</p>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Totals */}
        <div>
          <hr className="border-ink/30 my-3" />
          <div className="flex justify-between font-mono text-sm">
            <span>{t('order.subtotal')}</span><Price amountCents={order.totals.subtotal} />
          </div>
          {order.totals.discountAmountCents && order.totals.discountAmountCents > 0 ? (
            <div className="flex justify-between font-mono text-sm">
              <span>{t('order.discountLabel')} −{order.totals.discountPercent ?? 0}%</span>
              <Price amountCents={-order.totals.discountAmountCents} />
            </div>
          ) : null}
          {order.totals.loyaltyDiscountCents && order.totals.loyaltyDiscountCents > 0 ? (
            <div className="flex justify-between font-mono text-sm" data-testid="loyalty-discount-line">
              <span>{t('loyalty.checkout.discountLine')}</span>
              {/* Même signe « − » que l'encart du paiement où l'avantage a été choisi. */}
              <span className="font-mono font-bold text-ink whitespace-nowrap">
                <bdi dir="ltr">−{formatPrice(order.totals.loyaltyDiscountCents)}</bdi>
              </span>
            </div>
          ) : null}
          {order.totals.deliveryFee > 0 && (
            <div className="flex justify-between font-mono text-sm">
              <span>{t('order.deliveryLine')}</span><Price amountCents={order.totals.deliveryFee} />
            </div>
          )}
          <div className="flex justify-between font-display text-lg mt-2">
            <span>{t('order.total')}</span><Price amountCents={order.totals.total} />
          </div>
        </div>

        {/* Delivery address */}
        {order.mode === 'delivery' && order.address && (
          <div className="font-mono text-sm space-y-1">
            <p className="font-display text-base">{t('orderDetail.deliveryTo')}</p>
            <p>{order.address.street}</p>
            <p>{order.address.zip} {order.address.city}</p>
            {order.address.instructions && <p>{order.address.instructions}</p>}
          </div>
        )}

        {/* Customer */}
        {(order.customer.name || order.customer.phone) && (
          <p className="font-mono text-sm">
            {order.customer.name}{order.customer.name && order.customer.phone ? ' · ' : ''}{order.customer.phone}
          </p>
        )}

        {/* Reconfirmation banner: catalog has changed since this order. */}
        {pending && pending.skipped.length > 0 && (
          <div
            data-testid="reorder-skipped-banner"
            className="border-2 border-ink shadow-stamp-sm rounded-btn p-4 space-y-3 bg-paper"
          >
            <p className="font-display text-base">
              {t('orderDetail.itemsUnavailable')}
            </p>
            <ul className="font-mono text-sm list-disc pl-5 space-y-1">
              {pending.skipped.map((name, idx) => (
                <li key={`${name}-${idx}`}>{name}</li>
              ))}
            </ul>
            {pending.resolved.length > 0 ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => commit(pending.resolved)}
                  className="flex-1 py-2 bg-ink text-paper font-display rounded-btn shadow-stamp-sm"
                >
                  {t('orderDetail.continueAvailable')}
                </button>
                <button
                  type="button"
                  onClick={() => setPending(null)}
                  className="px-4 py-2 border-2 border-ink font-display rounded-btn"
                >
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setPending(null)}
                className="w-full py-2 border-2 border-ink font-display rounded-btn"
              >
                {t('common.close')}
              </button>
            )}
          </div>
        )}

        {/* Recommander + back link */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleRecommander}
            disabled={reorderDisabled}
            title={reorderTitle}
            className={`w-full py-3 bg-ink text-paper font-display rounded-btn ${reorderDisabled ? 'opacity-40 cursor-not-allowed' : 'shadow-stamp-sm'}`}
          >
            {t('orderDetail.reorder')}
          </button>
          <Link to={backLink} className="block text-center text-sm underline">
            {t('orderDetail.backToOrders')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper border-2 border-ink shadow-stamp p-6 rounded-btn text-center space-y-3">
      <p className="font-display text-ink">{t('account.orders.notFound')}</p>
      <Link to={backLink} className="underline text-sm">{t('orderDetail.backToOrders')}</Link>
    </div>
  );
}
